import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, ChevronRight } from 'lucide-react';
import { FamilyMember, Post } from '../types';
import {
  search,
  getSearchSuggestions,
  getRecentSearches,
  saveRecentSearch,
  clearRecentSearches,
  SearchResult,
} from '../services/searchService';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: Record<string, FamilyMember>;
  posts: Post[];
  onSelectMember?: (memberId: string) => void;
  onSelectPost?: (post: Post) => void;
}

export default function SearchModal({
  isOpen,
  onClose,
  members,
  posts,
  onSelectMember,
  onSelectPost,
}: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);

  // Load recent searches on mount
  useEffect(() => {
    if (isOpen) {
      setRecentSearches(getRecentSearches());
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  // Handle search input
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSuggestions([]);
      setSelectedIndex(-1);
      return;
    }

    // Get results
    const searchResults = search(members, posts, query);
    setResults(searchResults);

    // Get suggestions
    const searchSuggestions = getSearchSuggestions(members, query);
    setSuggestions(searchSuggestions);

    setSelectedIndex(-1);
  }, [query, members, posts]);

  const handleSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    saveRecentSearch(searchQuery);
    setQuery(searchQuery);
    setRecentSearches(getRecentSearches());
  };

  const handleSelectResult = (result: SearchResult) => {
    saveRecentSearch(query);

    if (result.type === 'member' && onSelectMember) {
      onSelectMember(result.id);
    } else if (result.type === 'post' && onSelectPost) {
      onSelectPost(result.data as Post);
    }

    setQuery('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < results.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelectResult(results[selectedIndex]);
      }
    }
  };

  // Auto-scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsListRef.current) {
      const items = resultsListRef.current.querySelectorAll('[data-result-item]');
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-96 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search members, posts, dates..."
              className="flex-1 text-lg outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Results or Empty State */}
        <div className="flex-1 overflow-y-auto">
          {results.length > 0 ? (
            <div ref={resultsListRef} className="divide-y">
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  data-result-item
                  onClick={() => handleSelectResult(result)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                    index === selectedIndex ? 'bg-blue-50' : ''
                  }`}
                >
                  {/* Avatar */}
                  {result.avatar ? (
                    <img
                      src={result.avatar}
                      alt={result.title}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-gray-600 font-semibold">
                      {result.title[0].toUpperCase()}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {result.title}
                    </div>
                    {result.subtitle && (
                      <div className="text-sm text-gray-500 truncate">
                        {result.subtitle}
                      </div>
                    )}
                  </div>

                  {/* Type Badge */}
                  <div className="flex-shrink-0 ml-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      result.type === 'member'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {result.type === 'member' ? 'Member' : 'Post'}
                    </span>
                  </div>

                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : query.trim() ? (
            <div className="p-8 text-center text-gray-500">
              <p className="mb-2">No results found for "{query}"</p>
              <p className="text-sm">Try a different search term</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">
                    Suggestions
                  </h3>
                  <div className="space-y-1">
                    {suggestions.map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => handleSearch(suggestion)}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 rounded transition-colors flex items-center gap-2"
                      >
                        <Search className="w-4 h-4 text-gray-400" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between px-2 mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase">
                      Recent
                    </h3>
                    <button
                      onClick={() => {
                        clearRecentSearches();
                        setRecentSearches([]);
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map(recent => (
                      <button
                        key={recent}
                        onClick={() => handleSearch(recent)}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 rounded transition-colors flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4 text-gray-400" />
                        {recent}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {suggestions.length === 0 && recentSearches.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Start typing to search members and posts</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between">
          <div className="flex gap-4">
            <span>↑↓ Navigate</span>
            <span>Enter Select</span>
            <span>Esc Close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
